'use client';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { prefixPath } from '@/hooks/useLocalePath';
import { addFamilyMember } from '@/utils/user_service';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { calculateAgeFromBob } from '@/utils/lm_utilities';
import StyledDatePicker from './StyledDatePicker';
import dayjs from 'dayjs';
import { useTranslation } from '@/contexts/TranslationContext';

const FamilyMemberForm = ({ closeBtn = false, handleClose }) => {
  const pathname = usePathname();
  const session = useSessionContext();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    dob: null,
    gender: 'm',
    name: '',
  });

  const [memberAdded, setMemberAdded] = useState(false);

  const [validations, setValidations] = useState({
    name: '',
    dob: '',
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // clear individual validation on input change
    setValidations((prev) => ({ ...prev, [name]: '' }));

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle date change for DatePicker
  const handleDateChange = (newValue) => {
    const value = newValue ? newValue.format('YYYY-MM-DD') : '';
    handleInputChange({
      target: { name: 'dob', value },
    });
  };

  // -------------------------------
  // Validation Functions
  // -------------------------------

  const validateName = () => {
    const name = formData.name.trim();
    let isValid = true;
    let validationMsg = '';

    if (!name) {
      validationMsg = t('familyMember.nameRequired');
      isValid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(name)) {
      validationMsg = t('familyMember.nameInvalid');
      isValid = false;
    }

    if (!isValid) {
      setValidations((prev) => ({ ...prev, name: validationMsg }));
    }

    return isValid;
  };

  const validateDoB = () => {
    const dob = formData.dob;
    let isValid = true;
    let validationMsg = '';

    if (!dob) {
      validationMsg = t('baseChart.validation.selectDob');
      isValid = false;
    } else if (dob && calculateAgeFromBob(dob) < 15 && calculateAgeFromBob(dob) > 100) {
      validationMsg = t('familyMember.ageRange');
      isValid = false;
    }

    if (!isValid) {
      setValidations((prev) => ({ ...prev, dob: validationMsg }));
    }

    return isValid;
  };

  // -------------------------------
  // Submit
  // -------------------------------
  const submitForm = () => {
    const isNameValid = validateName();
    const isDobValid = validateDoB();

    if (!isNameValid || !isDobValid) {
      return;
    }

    addFamilyMember(formData)
      .then((res) => {
        if (res.status === 200) {
          setMemberAdded(true);
          setFormData({
            dob: null,
            gender: 'm',
            name: '',
          });
          setValidations({ name: '', dob: '' });
        } else if (res.status === 401 || res.status === 403) {
          window.location.href = prefixPath(pathname, '/signin');
        } else {
          alert(t('familyMember.addError'));
        }
      })
      .catch((err) => console.log(err));
  };

  return (
    <div className="app-content-body pb-0">
      <div className="wrapper-md p-0">
        <div className="row">
          <div className="col-12">
            <form
              name="FamilyMemberForm"
              className="form-horizontal form-validation"
              onSubmit={(e) => e.preventDefault()}
            >
              {memberAdded && (
                <div className="alert alert-success" dismiss-on-timeout="250">
                  {t('familyMember.addSuccess')}
                </div>
              )}

              <div className="cardpanel panel-default p-0 mb-0" style={{ border: '1px solid black' }}>
                <div className="card-title panel-heading p-3" style={{ border: '1px solid #edf1f2' }}>
                  <strong>{t('familyMember.heading')}</strong>
                </div>

                <div className="card-body panel-body p-4" style={{ background: 'white' }}>
                  {/* Name Field */}
                  <div className="row form-group">
                    <div className="col-4">
                      <label className="control-label">{t('familyMember.nameLabel')}</label>
                    </div>
                    <div className="col-7">
                      <div className="input-wrapper">
                        <input
                          type="text"
                          className={`form-control ${validations.name ? 'error-border' : ''}`}
                          name="name"
                          placeholder={t('familyMember.namePlaceholder')}
                          autoComplete="off"
                          onChange={handleInputChange}
                          value={formData.name}
                          style={{ outline: 'none', border: 'none', color: '#7C5032' }}
                        />
                        {validations.name && <span className="error-text">{validations.name}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="line line-dashed b-b line-lg"></div>

                  {/* Date of Birth */}
                  <div className="row form-group">
                    <div className="col-4">
                      <label className="control-label">{t('form.dateOfBirth')}</label>
                    </div>
                    <div className="col-7 i_measurement-wrapper position-relative">
                      <div className="input-wrapper" style={{ background: 'white' }}>
                        <StyledDatePicker
                          name="dob"
                          id="dob"
                          className={`dob text-dark form-control ${validations.dob && 'error-border'}`}
                          value={formData.dob ? dayjs(formData.dob) : null}
                          onChange={handleDateChange}
                          iconColor={'#7C5032'}
                        />
                        {validations.dob && <span className="error-text">{validations.dob}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="line line-dashed b-b line-lg"></div>

                  {/* Gender */}
                  <div className="row form-group">
                    <div className="col-3">
                      <label className="control-label">{t('form.gender')}</label>
                    </div>
                    <div className="col-9">
                      <div className="form-group">
                        <label className="i-checks">
                          <input
                            type="radio"
                            name="gender"
                            value="m"
                            checked={formData.gender === 'm'}
                            onChange={handleInputChange}
                          />
                          <i></i> {t('familyMember.male')}
                        </label>
                        &nbsp;&nbsp;
                        <label className="i-checks">
                          <input
                            type="radio"
                            name="gender"
                            value="f"
                            checked={formData.gender === 'f'}
                            onChange={handleInputChange}
                          />
                          <i></i> {t('familyMember.female')}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="card-footer panel-footer p-4"
                  style={{ border: '1px solid #edf1f2', display: 'flex', justifyContent: 'space-between' }}
                >
                  <button type="button" onClick={submitForm} className="btn btn-success">
                    {t('familyMember.submit')}
                  </button>

                  {closeBtn && (
                    <button
                      onClick={() => handleClose()}
                      style={{
                        background: 'linear-gradient(180deg, #FFAB00 0%, #FFC040 100%)',
                        padding: '8px 4rem',
                        borderRadius: '3rem',
                        border: 'none',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      {t('familyMember.close')}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyMemberForm;
